import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useCreateAdminQuizMutation, useGetAdminQuizDifficultiesQuery } from "../../../lib/apollo/graphql/quiz/quiz-generated";
import BasicButton from "../../common/components/BasicButton";
import Input from "../../common/components/Input";
import Checkbox from "../../common/components/Checkbox";
import { Bars3Icon, MinusIcon, PlusIcon, ChevronLeftIcon, CloudArrowUpIcon, ListBulletIcon, CheckCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface QuizOption {
  id: string;
  content: string;
  isCorrect: boolean;
}

interface ShortAnswer {
  id: string;
  content: string;
}

interface DifficultyData {
  id: string;
  code: string;
  label: string;
  points: number;
  order: number;
}

interface SortableItemProps {
  option: QuizOption;
  index: number;
  onOptionChange: (index: number, value: string) => void;
  onCorrectChange: (index: number, isCorrect: boolean) => void;
  onRemoveOption: (index: number) => void;
  canRemove: boolean;
}

function SortableItem({
  option,
  index,
  onOptionChange,
  onCorrectChange,
  onRemoveOption,
  canRemove,
}: SortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: option.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="flex items-center bg-white">
      {/* 문제번호/드래그 핸들 */}
      <div className="w-16 flex items-center justify-center py-2 border-r border-gray-200">
        <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing">
          <Bars3Icon className="h-5 w-5 text-gray-400" />
        </div>
      </div>

      {/* 정답여부 체크박스 */}
      <div className="w-20 flex items-center justify-center py-2 border-r border-gray-200">
        <div
          className={`w-4 h-4 cursor-pointer rounded-sm border transition-colors ${
            option.isCorrect
              ? "bg-blue-600 border-blue-600"
              : "border-gray-300 bg-white"
          }`}
          onClick={() => onCorrectChange(index, !option.isCorrect)}
        >
          {option.isCorrect && (
            <svg className="w-full h-full text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          )}
        </div>
      </div>

      {/* 보기 내용 */}
      <div className="flex-1 py-2 px-4 border-r border-gray-200">
        <input
          type="text"
          value={option.content}
          onChange={(e) => onOptionChange(index, e.target.value)}
          className="w-full px-0 py-1 border-0 focus:outline-none focus:ring-0 bg-transparent"
          placeholder="보기 내용을 입력하세요"
        />
      </div>

      {/* 삭제 버튼 */}
      <div className="w-16 flex items-center justify-center py-2">
        <button
          onClick={() => onRemoveOption(index)}
          disabled={!canRemove}
          className={`w-7 h-7 rounded flex items-center justify-center transition-colors ${
            !canRemove
              ? "bg-gray-300 cursor-not-allowed"
              : "bg-red-500 hover:bg-red-600"
          }`}
        >
          <MinusIcon className="h-4 w-4 text-white" />
        </button>
      </div>
    </div>
  );
}

export default function QuizCreate() {
  const navigate = useNavigate();
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [createQuiz, { loading: creating }] = useCreateAdminQuizMutation();

  // 활성 난이도 조회
  const { data: difficultiesData } = useGetAdminQuizDifficultiesQuery({
    variables: { includeInactive: false },
    fetchPolicy: "network-only",
  });

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [type, setType] = useState<"객관식" | "O/X" | "단답형">("객관식");
  const [selectedDifficultyId, setSelectedDifficultyId] = useState<string>("");
  const [points, setPoints] = useState(10);
  const [question, setQuestion] = useState("");
  const [hint, setHint] = useState("");
  const [options, setOptions] = useState<QuizOption[]>([
    { id: "1", content: "", isCorrect: false },
    { id: "2", content: "", isCorrect: false }
  ]);
  const [oxAnswer, setOxAnswer] = useState<"O" | "X">("O");
  const [shortAnswers, setShortAnswers] = useState<ShortAnswer[]>([
    { id: "1", content: "" }
  ]);
  const [typoTolerance, setTypoTolerance] = useState(0);
  const [explanation, setExplanation] = useState("");

  // 활성화된 난이도 목록을 order 순으로 정렬
  const difficulties: DifficultyData[] = difficultiesData?.difficulties
    ? [...difficultiesData.difficulties].sort((a, b) => a.order - b.order)
    : [];

  // 첫 번째 난이도를 기본값으로 설정
  useEffect(() => {
    if (difficulties.length > 0 && !selectedDifficultyId) {
      setSelectedDifficultyId(difficulties[0].id);
      setPoints(difficulties[0].points);
    }
  }, [difficulties, selectedDifficultyId]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = options.findIndex(option => option.id === active.id);
      const newIndex = options.findIndex(option => option.id === over.id);

      const newOptions = arrayMove(options, oldIndex, newIndex);
      setOptions(newOptions);
    }
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleFileSelect = (file: File) => {
    if (file.type === "application/pdf") {
      setSelectedFile(file);
    } else {
      alert("PDF 파일만 업로드 가능합니다.");
    }
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);
    const file = event.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleOptionChange = (index: number, value: string) => {
    const newOptions = [...options];
    newOptions[index] = { ...newOptions[index], content: value };
    setOptions(newOptions);
  };

  const handleCorrectChange = (index: number, isCorrect: boolean) => {
    const newOptions = options.map((option, i) => ({
      ...option,
      isCorrect: i === index ? isCorrect : false
    }));
    setOptions(newOptions);
  };

  const handleAddOption = () => {
    if (options.length < 5) {
      const newOption = {
        id: Date.now().toString(),
        content: "",
        isCorrect: false
      };
      setOptions([...options, newOption]);
    }
  };

  const handleRemoveOption = (index: number) => {
    if (options.length > 2) {
      const newOptions = options.filter((_, i) => i !== index);
      setOptions(newOptions);
    }
  };

  const handleShortAnswerChange = (index: number, value: string) => {
    const newAnswers = [...shortAnswers];
    newAnswers[index] = { ...newAnswers[index], content: value };
    setShortAnswers(newAnswers);
  };

  const handleAddShortAnswer = () => {
    const newAnswer = {
      id: Date.now().toString(),
      content: ""
    };
    setShortAnswers([...shortAnswers, newAnswer]);
  };

  const handleRemoveShortAnswer = (index: number) => {
    if (shortAnswers.length > 1) {
      const newAnswers = shortAnswers.filter((_, i) => i !== index);
      setShortAnswers(newAnswers);
    }
  };

  const handleDifficultySelect = (difficultyId: string) => {
    setSelectedDifficultyId(difficultyId);
    const selectedDifficulty = difficulties.find(d => d.id === difficultyId);
    if (selectedDifficulty) {
      setPoints(selectedDifficulty.points);
    }
  };

  const handleSave = async () => {
    // 질문 검증
    if (!question.trim()) {
      alert("질문을 입력해주세요.");
      return;
    }

    // 보기 검증 (객관식인 경우)
    if (type === "객관식") {
      const hasEmptyOption = options.some(option => !option.content.trim());
      if (hasEmptyOption) {
        alert("보기 내용을 입력해주세요.");
        return;
      }

      // 정답 체크 검증
      const hasCorrectAnswer = options.some(option => option.isCorrect);
      if (!hasCorrectAnswer) {
        alert("정답을 체크해주세요.");
        return;
      }
    }

    // 정답 검증 (단답형인 경우)
    if (type === "단답형") {
      if (shortAnswers.length === 0) {
        alert("정답을 최소 1개 이상 입력해주세요.");
        return;
      }
      const hasEmptyAnswer = shortAnswers.some(answer => !answer.content.trim());
      if (hasEmptyAnswer) {
        alert("정답 내용을 입력해주세요.");
        return;
      }
    }

    // 해설 검증
    if (!explanation.trim()) {
      alert("해설을 입력해주세요.");
      return;
    }

    // 저장 확인
    if (!window.confirm("저장하시겠습니까?")) {
      return;
    }

    try {
      // Map type to API format
      const quizType = type === "객관식" ? "MCQ" : type === "O/X" ? "OX" : "SHORT";

      // Prepare input
      const input: any = {
        sourceMaterialId: null, // Optional - will be added when document upload is implemented
        type: quizType,
        stem: question,
        explanation,
        hint: hint.trim() || undefined,
        difficultyId: selectedDifficultyId,
        awardedPoints: points,
      };

      // Add type-specific fields
      if (type === "객관식" || type === "O/X") {
        const labels = ["A", "B", "C", "D", "E"];
        input.choices = options.map((option, index) => ({
          label: labels[index],
          text: option.content,
          isCorrect: option.isCorrect,
        }));
      }

      if (type === "단답형") {
        input.shortAnswerSpec = {
          acceptableAnswers: shortAnswers.map(a => a.content),
          typoTolerance,
        };
      }

      const result = await createQuiz({
        variables: { input },
      });

      if (result.data) {
        alert("퀴즈가 생성되었습니다.");
        navigate("/quiz/management");
      }
    } catch (error: any) {
      console.error("Quiz creation error:", error);
      alert(error.message || "퀴즈 생성에 실패했습니다.");
    }
  };

  const handleCancel = () => {
    if (window.confirm("작성을 취소하고 나가시겠습니까?")) {
      navigate(-1);
    }
  };

  return (
    <div className="bg-white rounded-lg p-6 border border-gray-200" style={{ minWidth: '800px' }}>
      {/* 헤더 영역 */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={handleGoBack}
            className="flex items-center justify-center w-8 h-8 rounded hover:bg-gray-100 transition-colors"
          >
            <ChevronLeftIcon className="w-5 h-5 text-gray-700" />
          </button>
          <h1 className="text-2xl font-bold">퀴즈 생성</h1>
        </div>
      </div>

      <div className="space-y-8">
        {/* 유형 */}
        <div>
          <h2 className="text-lg font-semibold mb-4 text-gray-800">유형</h2>
          <div className="flex gap-4">
            <div
              onClick={() => setType("객관식")}
              className={`flex items-center gap-3 p-4 rounded-lg border-2 transition-colors cursor-pointer flex-1 ${
                type === "객관식"
                  ? "border-blue-600 bg-blue-50"
                  : "border-gray-300 bg-white hover:bg-gray-50"
              }`}
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <ListBulletIcon className="h-5 w-5 text-blue-600" />
              </div>
              <span className="text-sm font-medium text-gray-900">
                객관식
              </span>
            </div>
            <div
              onClick={() => setType("O/X")}
              className={`flex items-center gap-3 p-4 rounded-lg border-2 transition-colors cursor-pointer flex-1 ${
                type === "O/X"
                  ? "border-blue-600 bg-blue-50"
                  : "border-gray-300 bg-white hover:bg-gray-50"
              }`}
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <CheckCircleIcon className="h-5 w-5 text-blue-600" />
              </div>
              <span className="text-sm font-medium text-gray-900">
                O/X
              </span>
            </div>
            <div
              onClick={() => setType("단답형")}
              className={`flex items-center gap-3 p-4 rounded-lg border-2 transition-colors cursor-pointer flex-1 ${
                type === "단답형"
                  ? "border-blue-600 bg-blue-50"
                  : "border-gray-300 bg-white hover:bg-gray-50"
              }`}
            >
              <div className="w-4 h-4 border border-blue-600 rounded flex items-center justify-center">
                <MinusIcon className="h-2.5 w-2.5 text-blue-600" />
              </div>
              <span className="text-sm font-medium text-gray-900">
                단답형
              </span>
            </div>
          </div>
        </div>

        {/* 난이도 섹션 */}
        <div>
          <h2 className="text-lg font-semibold mb-4 text-gray-800">난이도</h2>
          <div className="flex gap-4">
            {difficulties.map((difficulty) => (
              <div
                key={difficulty.id}
                onClick={() => handleDifficultySelect(difficulty.id)}
                className={`flex items-center gap-3 p-4 rounded-lg border-2 transition-colors cursor-pointer flex-1 ${
                  selectedDifficultyId === difficulty.id
                    ? "border-blue-600 bg-blue-50"
                    : "border-gray-300 bg-white hover:bg-gray-50"
                }`}
              >
                <div className="w-5 h-5 rounded border-2 border-blue-600 bg-blue-600 flex items-center justify-center">
                  <span className="text-white text-xs font-bold">
                    {difficulty.order}
                  </span>
                </div>
                <span className="text-sm font-medium text-gray-900">
                  {difficulty.label}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* 획득 포인트 */}
        <div>
          <h2 className="text-lg font-semibold mb-4 text-gray-800">획득 포인트</h2>
          <input
            type="number"
            value={points}
            onChange={(e) => setPoints(Number(e.target.value))}
            className="w-40 h-[40px] px-3 border border-gray-400 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            min="0"
            placeholder="포인트"
          />
        </div>

        {/* 질문 섹션 */}
        <div>
          <h2 className="text-lg font-semibold mb-4 text-gray-800">질문</h2>
          <textarea
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            className="w-full h-24 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
            placeholder="질문을 입력하세요"
          />
        </div>

        {/* 보기 섹션 - 객관식일 때만 표시 */}
        {type === "객관식" && (
          <div>
            <h2 className="text-lg font-semibold mb-2 text-gray-800">보기</h2>
            <p className="text-xs text-gray-500 mb-4">
              보기를 2개 이상 5개 이하로 추가하고 정답을 1개 이상 체크해주세요.
            </p>
            <div className="space-y-3">
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={options.map(option => option.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {options.map((option, index) => (
                      <div key={option.id} className="border-b border-gray-200 last:border-b-0">
                        <SortableItem
                          option={option}
                          index={index}
                          onOptionChange={handleOptionChange}
                          onCorrectChange={handleCorrectChange}
                          onRemoveOption={handleRemoveOption}
                          canRemove={options.length > 2}
                        />
                      </div>
                    ))}
                  </SortableContext>
                </DndContext>

                <div className="flex justify-end py-3 px-4 bg-white">
                  <button
                    onClick={handleAddOption}
                    disabled={options.length >= 5}
                    className={`flex items-center gap-2 px-4 py-2 text-sm rounded border transition-colors ${
                      options.length >= 5
                        ? "text-gray-400 bg-white border-gray-300 cursor-not-allowed"
                        : "text-gray-700 bg-gray-100 border-gray-300 hover:bg-gray-200"
                    }`}
                  >
                    <PlusIcon className="h-4 w-4" />
                    보기 추가
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 정답 섹션 - O/X일 때만 표시 */}
        {type === "O/X" && (
          <div>
            <h2 className="text-lg font-semibold mb-4 text-gray-800">정답</h2>
            <div className="flex gap-4">
              <div
                onClick={() => setOxAnswer("O")}
                className={`flex items-center justify-center p-6 rounded-lg border-2 transition-colors flex-1 cursor-pointer ${
                  oxAnswer === "O"
                    ? "border-blue-600 bg-blue-50"
                    : "border-gray-300 bg-white hover:bg-gray-50"
                }`}
              >
                <span className={`text-2xl ${oxAnswer === "O" ? "text-blue-600" : "text-gray-500"}`}>O</span>
              </div>
              <div
                onClick={() => setOxAnswer("X")}
                className={`flex items-center justify-center p-6 rounded-lg border-2 transition-colors flex-1 cursor-pointer ${
                  oxAnswer === "X"
                    ? "border-blue-600 bg-blue-50"
                    : "border-gray-300 bg-white hover:bg-gray-50"
                }`}
              >
                <span className={`text-2xl ${oxAnswer === "X" ? "text-blue-600" : "text-gray-500"}`}>X</span>
              </div>
            </div>
          </div>
        )}

        {/* 정답 섹션 - 단답형일 때만 표시 */}
        {type === "단답형" && (
          <div>
            <h2 className="text-lg font-semibold mb-2 text-gray-800">정답</h2>
            <p className="text-xs text-gray-500 mb-4">
              정답으로 인정할 내용을 모두 입력해주세요.
            </p>
            <div className="space-y-3">
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                {shortAnswers.map((answer, index) => (
                  <div key={answer.id} className="flex items-center bg-white border-b border-gray-200 last:border-b-0">
                    <div className="w-16 flex items-center justify-center py-2 border-r border-gray-200">
                      <span className="text-sm font-medium text-gray-700">{index + 1}</span>
                    </div>

                    <div className="flex-1 py-2 px-4 border-r border-gray-200">
                      <input
                        type="text"
                        value={answer.content}
                        onChange={(e) => handleShortAnswerChange(index, e.target.value)}
                        className="w-full px-0 py-1 border-0 focus:outline-none focus:ring-0 bg-transparent"
                        placeholder="정답 내용을 입력하세요"
                      />
                    </div>

                    <div className="w-16 flex items-center justify-center py-2">
                      <button
                        onClick={() => handleRemoveShortAnswer(index)}
                        disabled={shortAnswers.length <= 1}
                        className={`w-7 h-7 rounded flex items-center justify-center transition-colors ${
                          shortAnswers.length <= 1
                            ? "bg-gray-300 cursor-not-allowed"
                            : "bg-red-500 hover:bg-red-600"
                        }`}
                      >
                        <MinusIcon className="h-4 w-4 text-white" />
                      </button>
                    </div>
                  </div>
                ))}

                <div className="flex justify-end py-3 px-4 bg-white">
                  <button
                    onClick={handleAddShortAnswer}
                    className="flex items-center gap-2 px-4 py-2 text-sm rounded border transition-colors text-gray-700 bg-gray-100 border-gray-300 hover:bg-gray-200"
                  >
                    <PlusIcon className="h-4 w-4" />
                    정답 추가
                  </button>
                </div>
              </div>
            </div>

            {/* 오타 허용 범위 */}
            <div className="mt-4">
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                오타 허용 범위
              </label>
              <input
                type="number"
                value={typoTolerance}
                onChange={(e) => setTypoTolerance(Number(e.target.value))}
                className="w-40 h-[40px] px-3 border border-gray-400 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="0"
                min="0"
              />
              <p className="text-xs text-gray-500 mt-1">
                정답과 사용자 답변 간 허용되는 문자 차이 개수
              </p>
            </div>
          </div>
        )}

        {/* 해설 섹션 */}
        <div>
          <h2 className="text-lg font-semibold mb-4 text-gray-800">해설</h2>
          <textarea
            value={explanation}
            onChange={(e) => setExplanation(e.target.value)}
            className="w-full h-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
            placeholder="해설을 입력하세요"
          />
        </div>

        {/* 힌트 섹션 */}
        <div>
          <h2 className="text-lg font-semibold mb-4 text-gray-800">힌트 (선택)</h2>
          <textarea
            value={hint}
            onChange={(e) => setHint(e.target.value)}
            className="w-full h-24 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
            placeholder="힌트를 입력하세요 (선택사항)"
          />
        </div>

        {/* 기반자료 섹션 (선택) */}
        <div>
          <h2 className="text-lg font-semibold mb-4 text-gray-800">기반자료 (선택)</h2>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            onChange={handleFileInputChange}
            className="hidden"
          />

          {!selectedFile ? (
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={handleUploadClick}
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                isDragOver
                  ? "border-blue-500 bg-blue-50"
                  : "border-gray-300 hover:border-gray-400 hover:bg-gray-50"
              }`}
            >
              <CloudArrowUpIcon className="h-12 w-12 mx-auto mb-4 text-gray-400" />
              <p className="text-sm text-gray-600 mb-2">
                클릭하거나 파일을 드래그하여 업로드하세요
              </p>
              <p className="text-xs text-gray-500">PDF 파일만 업로드 가능합니다</p>
            </div>
          ) : (
            <div className="border border-gray-300 rounded-lg p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded flex items-center justify-center">
                  <span className="text-red-600 text-xs font-bold">PDF</span>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">{selectedFile.name}</p>
                  <p className="text-xs text-gray-500">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedFile(null);
                }}
                className="p-2 hover:bg-gray-100 rounded transition-colors"
              >
                <XMarkIcon className="h-5 w-5 text-gray-500" />
              </button>
            </div>
          )}
        </div>

        {/* 버튼 영역 */}
        <div className="flex justify-end gap-3 pt-6">
          <BasicButton
            text="취소"
            variant="outlined"
            className="px-6 py-2 border-black text-black hover:bg-gray-100"
            onClick={handleCancel}
            disabled={creating}
          />
          <BasicButton
            text={creating ? "저장 중..." : "저장"}
            variant="primary"
            className="px-6 py-2 bg-black text-white hover:bg-gray-800"
            onClick={handleSave}
            disabled={creating}
          />
        </div>
      </div>
    </div>
  );
}
